import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useFormik } from 'formik';
import {
  PresentationRequestHeader,
  PresentationContainer,
} from '@Components/index';
import { FiltersWidget } from '@Components/FiltersWidget';
import { MSkeleton } from '@Components/Base';
import ContactList from '@Components/ContactList/ContactList';
import ContactFilters from '@Components/ContactFilters/ContactFilters';
import ContactViewWidget from '@Components/ContactList/components/ContactViewWidget';
import type {
  Contact,
  ContactFilters as ContactFiltersType,
} from '@Components/ContactList/types';
import type { SortConfig } from '@Features/types/SortConfig';
import { useSearchContactsMutation } from '@Store/api/contactApiSlice';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  useGetWorkLocationTypesQuery,
  useGetDeptProjListQuery,
} from '@Store/api/dictionaryApiSlice';
import { useAppDispatch } from '@Hooks/index';
import { dictionaryActions } from '@Store/slices/dictionarySlice';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import {
  ContactPageForm,
  initialValuesFilterForm,
} from '@Features/ContactPage/utils/InitialValue';
import { createSearchCriteriaList } from '@Features/ContactPage/utils/FilterCriteria';

const ContactPageLayout: React.FC = () => {
  const dispatch = useAppDispatch();

  // Load dictionary data
  const workLocationTypesDict = useGetWorkLocationTypesQuery();
  const deptProjList = useGetDeptProjListQuery();

  // Load dictionary data into store
  useLayoutEffect(() => {
    if (workLocationTypesDict?.data) {
      dispatch(
        dictionaryActions.setWorkLocationTypeDict(workLocationTypesDict.data)
      );
    }
  }, [workLocationTypesDict.data, dispatch]);

  useLayoutEffect(() => {
    if (deptProjList?.data) {
      dispatch(dictionaryActions.setDeptProjList(deptProjList.data));
    }
  }, [deptProjList.data, dispatch]);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(TABLE_PAGE_DEFAULT);
  const [dataSource, setDataSource] = useState<Contact[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filters, setFilters] = useState<ContactFiltersType>({});
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // Tag-related states
  const [showTags, setShowTags] = useState(false);
  const [tagsValues, setTagsValues] = useState<ContactPageForm>(
    initialValuesFilterForm
  );

  // Modal states
  const [isViewWidgetOpen, setIsViewWidgetOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const PAGE_SIZE = 20;

  // API mutation
  const [searchContacts, { isLoading }] = useSearchContactsMutation();

  const loadContacts = async (
    page: number = TABLE_PAGE_DEFAULT,
    searchQuery: string = '',
    filterValues: ContactFiltersType = {},
    sortConfiguration: SortConfig | null = null
  ): Promise<void> => {
    try {
      const searchCriteriaList = createSearchCriteriaList(
        searchQuery,
        filterValues
      );

      const searchRequest = {
        searchValue: searchQuery.trim(),
        searchCriteriaList,
        dataOperator: 'and',
      };

      // Handle sorting - use the field name directly from sortConfig
      const sortBy = sortConfiguration ? sortConfiguration.sortBy : 'empNumber';
      const sortOrder = sortConfiguration
        ? sortConfiguration.sortOrder || 'asc'
        : 'asc';

      const response = await searchContacts({
        page,
        size: PAGE_SIZE,
        'sort-by': sortBy,
        'sort-order': sortOrder,
        body: searchRequest,
      }).unwrap();

      setDataSource(response.content);
      setTotalItems(response.totalElements);
      setCurrentPage(response.page);
    } catch (error) {
      setDataSource([]);
      setTotalItems(0);
    }
  };

  // Load contacts on component mount
  useEffect(() => {
    const loadInitialData = async (): Promise<void> => {
      setIsContentLoaded(false);
      await loadContacts();
      setIsContentLoaded(true);
    };
    loadInitialData();
  }, []);

  // Handle search (including clearing search, same behavior as ESS)
  const handleSearch = async (searchQuery: string | null): Promise<void> => {
    const normalizedQuery = (searchQuery ?? '').trim();
    setSearchValue(normalizedQuery);
    setCurrentPage(TABLE_PAGE_DEFAULT);
    await loadContacts(
      TABLE_PAGE_DEFAULT,
      normalizedQuery,
      filters,
      sortConfig
    );
  };

  // Handle filter changes
  const handleFilterChange = async (
    newFilters: ContactFiltersType
  ): Promise<void> => {
    setFilters(newFilters);
    setCurrentPage(TABLE_PAGE_DEFAULT);
    await loadContacts(TABLE_PAGE_DEFAULT, searchValue, newFilters, sortConfig);
  };

  // Handle page change
  const handleChangePage = async (page: number): Promise<void> => {
    setCurrentPage(page);
    await loadContacts(page, searchValue, filters, sortConfig);
  };

  // Handle sort
  const handleSort = async (
    newSortConfig: SortConfig | null
  ): Promise<void> => {
    setSortConfig(newSortConfig);
    setCurrentPage(TABLE_PAGE_DEFAULT);
    await loadContacts(TABLE_PAGE_DEFAULT, searchValue, filters, newSortConfig);
  };

  // Handle view contact
  const handleView = (contact: Contact): void => {
    setSelectedContact(contact);
    setIsViewWidgetOpen(true);
  };

  // Handle edit contact
  const handleEdit = (): void => {
    // TODO: Implement edit functionality
  };

  // Handle close view widget
  const handleCloseViewWidget = (): void => {
    setIsViewWidgetOpen(false);
    setSelectedContact(null);
  };

  // Handle close filters
  const closeFilters = (): void => setIsFiltersOpen(false);

  // Formik for custom filters
  const customFormik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: (options: ContactPageForm) => {
      const filtersFromForm: ContactFiltersType = {
        employeeNumber: options.employeeNumber,
        employeeName: options.employeeName,
        occupation: options.occupation,
        email: options.email,
        mobile: options.mobile,
        extension: options.extension,
        workLocation: options.workLocation,
        department: options.department,
      };

      // Parse selected filters to options for tags
      const parsedOptions = parseSelectedFiltersToOptions(options);

      // Set filter options in store for tags
      dispatch(filterOptionsActions.setFilterOptions(parsedOptions));

      // Set tags values and show tags
      setTagsValues(options);
      setShowTags(true);

      // Apply filters and close modal
      handleFilterChange(filtersFromForm);
      closeFilters();
    },
  });

  // Formik for tags - this handles tag removal
  const tagsFormik = useFormik({
    initialValues: tagsValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      setTagsValues(values);

      // Update the custom formik (filters tab) with the same values
      customFormik.setValues(values);

      const filtersFromTags: ContactFiltersType = {
        employeeNumber: values.employeeNumber,
        employeeName: values.employeeName,
        occupation: values.occupation,
        email: values.email,
        mobile: values.mobile,
        extension: values.extension,
        workLocation: values.workLocation,
        department: values.department,
      };
      handleFilterChange(filtersFromTags);
    },
  });

  // Add a function to handle clear all that resets to proper default values
  const handleClearAll = (): void => {
    // Reset all fields to their default values
    const defaultValues: ContactPageForm = {
      search: '',
      employeeNumber: '',
      employeeName: '',
      occupation: '',
      extension: '',
      email: '',
      mobile: '',
      workLocation: 'all',
      department: '', // Set to empty string instead of 'all'
      departmentType: undefined,
    };

    // Update both formiks
    customFormik.setValues(defaultValues);
    tagsFormik.setValues(defaultValues);

    // Clear tags
    setTagsValues(defaultValues);
    setShowTags(false);

    // Clear filter options from store
    dispatch(filterOptionsActions.setFilterOptions([]));

    // Force formik to re-render by touching all fields
    customFormik.setTouched({
      search: false,
      employeeNumber: false,
      employeeName: false,
      occupation: false,
      extension: false,
      email: false,
      mobile: false,
      workLocation: false,
      department: false,
      departmentType: false,
    });

    // Clear filters and reload data
    setFilters({});
    handleFilterChange({});
  };

  const openFilters = (): void => setIsFiltersOpen(true);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PresentationRequestHeader
        caption="Contact List (Address Book)"
        showSelect={false}
      />
      <PresentationContainer fullHeight>
        <FiltersWidget
          onSearchSubmit={handleSearch}
          isOpen={isFiltersOpen}
          onClose={closeFilters}
          onOpen={openFilters}
          formik={tagsFormik}
          showTags={showTags}
          placeholder="Search by employee name, number or occupation..."
        >
          <ContactFilters formik={customFormik} onClearAll={handleClearAll} />
        </FiltersWidget>

        {isContentLoaded ? (
          <ContactList
            dataSource={dataSource}
            totalItems={totalItems}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            onChangePage={handleChangePage}
            onSort={handleSort}
            isLoading={isLoading}
            onView={handleView}
            onEdit={handleEdit}
            isSearchApplied={Boolean(searchValue)}
            isFiltersApplied={Boolean(
              Object.values(filters).some((value) => value && value !== 'all')
            )}
          />
        ) : (
          <MSkeleton />
        )}
      </PresentationContainer>

      <ContactViewWidget
        isOpen={isViewWidgetOpen}
        contact={selectedContact}
        onClose={handleCloseViewWidget}
      />
    </div>
  );
};

export default ContactPageLayout;
